import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Slider } from 'antd';
import {
  CaretRightOutlined,
  PauseOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import { useAudioPlayerGroup } from './AudioPlayerGroup';
import styles from './style.module.css';

export interface AudioPlayerProps {
  src: string;
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AudioPlayer: React.FC<AudioPlayerProps> = memo(({ src }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const group = useAudioPlayerGroup();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(75);
  const [isMuted, setIsMuted] = useState(false);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }
  }, []);

  const handleCanPlay = useCallback(() => {
    const audio = audioRef.current;
    if (audio && isFinite(audio.duration) && duration === 0) {
      setDuration(audio.duration);
    }
  }, [duration]);

  const rafRef = useRef<number>(0);
  const isSeeking = useRef(false);

  useEffect(() => {
    const tick = () => {
      const audio = audioRef.current;
      if (audio && !isSeeking.current) {
        setCurrentTime(audio.currentTime);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        group?.registerPlay(pause);
        await audio.play();
        setIsPlaying(true);
      }
    } catch {
      // play() rejected — e.g. no user interaction yet
    }
  }, [isPlaying, group, pause]);

  const handleSeek = useCallback((value: number) => {
    isSeeking.current = true;
    setCurrentTime(value);
  }, []);

  const handleSeekComplete = useCallback((value: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = value;
    }
    isSeeking.current = false;
  }, []);

  const handleVolumeChange = useCallback((value: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = value / 100;
      setVolume(value);
      if (value > 0) {
        setIsMuted(false);
      }
    }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume / 100;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  return (
    <div className={styles.playerContainer}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onEnded={handleEnded}
      />

      <button className={styles.playButton} onClick={togglePlay} type="button">
        {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>

      <div className={styles.progressSection}>
        <Slider
          min={0}
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          onChangeComplete={handleSeekComplete}
          tooltip={{ open: false }}
          step={0.1}
        />
      </div>

      <span className={styles.timeDisplay}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      <button
        className={styles.volumeButton}
        onClick={toggleMute}
        type="button"
      >
        <SoundOutlined />
      </button>

      <div className={styles.volumeSlider}>
        <Slider
          min={0}
          max={100}
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          tooltip={{ open: false }}
        />
      </div>
    </div>
  );
});

AudioPlayer.displayName = 'AudioPlayer';
